import { Table } from "@tanstack/react-table";
import { Button } from "../button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../select";
import clsx from "clsx";
import { Icon } from "../icon";
import { ROWS_PER_PAGE_OPTIONS } from "~/lib/utils/constants";

const buttonClasses = {
  base: "h-7 w-7 !border-muted-foreground/50 !text-card-foreground",
  interactive:
    "hover:!text-foreground hover:!border-foreground disabled:opacity-50 active:border-[1.5px]",
};

export const DataTablePagination = <TData,>({
  table,
  rowsPerPageOptions,
  name,
}: {
  table: Table<TData>;
  rowsPerPageOptions?: number[];
  name: string;
}) => {
  const rowsOptions = rowsPerPageOptions || ROWS_PER_PAGE_OPTIONS;

  // Calculate the range of items being displayed
  const pageSize = table.getState().pagination.pageSize;
  const pageIndex = table.getState().pagination.pageIndex;
  const totalRows = table.getRowCount();

  const rangeStart = pageSize * pageIndex + 1;
  const rangeEnd = Math.min(rangeStart + pageSize - 1, totalRows);

  return (
    <div className="flex flex-col items-start justify-between gap-4 p-2 overflow-hidden rounded-lg md:items-center md:gap-0 md:flex-row bg-card md:max-h-10">
      <div className="flex-1 text-sm text-muted-foreground">
        {rangeStart}-{rangeEnd} de {totalRows}{" "}
        {totalRows === 1 ? name : `${name}s`}
      </div>

      <div className="flex items-center gap-8 ml-auto text-card-foreground">
        <Select
          value={String(table.getState().pagination.pageSize)}
          onValueChange={(value) => {
            table.setPageSize(Number(value));
          }}
        >
          <SelectTrigger className="flex items-center gap-1 h-7 !w-fit ring-muted-foreground/50">
            <SelectValue placeholder={table.getState().pagination.pageSize} />
            filas
          </SelectTrigger>
          <SelectContent side="top" className="min-w-0">
            {rowsOptions.map((pageSize) => (
              <SelectItem
                key={pageSize}
                value={String(pageSize)}
                className="h-7"
              >
                {pageSize}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <div className="flex items-center justify-center text-sm font-medium truncate">
          Página {table.getState().pagination.pageIndex + 1} de{" "}
          {table.getPageCount()}
        </div>

        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            className={clsx(
              "!p-1",
              buttonClasses.base,
              buttonClasses.interactive
            )}
            onClick={() => table.setPageIndex(0)}
            disabled={!table.getCanPreviousPage()}
          >
            <span className="sr-only">Go to first page</span>
            <Icon name="chevrons-left" />
          </Button>
          <Button
            variant="outline"
            className={clsx(
              "!p-1.5",
              buttonClasses.base,
              buttonClasses.interactive
            )}
            onClick={() => table.previousPage()}
            disabled={!table.getCanPreviousPage()}
          >
            <span className="sr-only">Go to previous page</span>
            <Icon name="chevron-left" />
          </Button>
          <Button
            variant="outline"
            className={clsx(
              "!p-1.5",
              buttonClasses.base,
              buttonClasses.interactive
            )}
            onClick={() => table.nextPage()}
            disabled={!table.getCanNextPage()}
          >
            <span className="sr-only">Go to next page</span>
            <Icon name="chevron-right" />
          </Button>
          <Button
            variant="outline"
            className={clsx(
              "!p-1",
              buttonClasses.base,
              buttonClasses.interactive
            )}
            onClick={() => table.setPageIndex(table.getPageCount() - 1)}
            disabled={!table.getCanNextPage()}
          >
            <span className="sr-only">Go to last page</span>
            <Icon name="chevrons-right" />
          </Button>
        </div>
      </div>
    </div>
  );
};
